import React, { memo, useMemo } from 'react';
import { connectHitInsights, connectHits, connectPagination } from 'react-instantsearch-dom';
import { ProductCard } from '~/shared/components';
import { StyledProductGrid } from './styled';
import { mapFromAlgolia } from '../../utils';
import { FormattedAlgoliaProductType } from '../../types';
import aa from 'search-insights';
import { usePage } from '$templates/pages/hooks/usePage';
import { useFranchise, useTranslation } from '~/shared/utils';
import { useCookieConsent } from '~/shared/hooks';
import { Pagination } from '../Pagination';
import {
    M170SearchPage,
    P40ProductListPage,
    P41ProductListPage,
    MarketingSpot as MarketingSpotType,
} from '~/lib/data-contract';
import { usePLPGridLayout } from '../../hooks/usePLPGridLayout';
import { AlgoliaHit, ProductHitsType, ProductInsightsType } from '../../models';
import { useSource } from '~/shared/utils/gtm';
import { useSchemaMarkupCollectionPage } from '$templates/pages';
import { Headline } from '~/templates/blocks/components/Headline/Headline';
import { weakKey } from '~/shared/utils/jsx';
import { MarketingSpot } from '~/templates';

const ProductCardWithInsights = (props: ProductInsightsType) => {
    const { hit: product, insights } = props;
    const franchise = useFranchise();
    const { statistic } = useCookieConsent();

    const formattedProduct: FormattedAlgoliaProductType = useMemo(
        () => mapFromAlgolia(product, franchise, parseInt(product.__position)),
        [franchise, product]
    );

    return (
        <ProductCard
            product={formattedProduct}
            queryId={statistic ? product.__queryID : undefined}
            insights={insights}
            listType={product.listType}
            index={product.index}
            isProductListPage={product.isProductListPage}
        />
    );
};

const HitWithInsights = memo(connectHitInsights(aa)(ProductCardWithInsights));

/* Guards */
const isHit = (hit: AlgoliaHit | MarketingSpotType): hit is AlgoliaHit =>
    (hit as AlgoliaHit).productId !== undefined;
const isSpot = (hit: AlgoliaHit | MarketingSpotType): hit is MarketingSpotType =>
    (hit as MarketingSpotType).position !== undefined;

/* Algolia Products Map */
const CustomHit = ({ hits, isProductListPage, marketingContent }: ProductHitsType) => {
    const { type } = usePage<P40ProductListPage | P41ProductListPage | M170SearchPage>();
    const { translate } = useTranslation();
    const { source } = useSource();
    const gridLayout = usePLPGridLayout(type);
    const collectionPageSchema = useSchemaMarkupCollectionPage(hits.slice(0, 8));

    const isZeroResults = hits.length === 0;

    // Insert marketing spots into the hits array
    const hitsWithPotientialSpots = useMemo<(AlgoliaHit | MarketingSpotType)[]>(() => {
        if (!marketingContent || marketingContent.length === 0) {
            return hits;
        }

        // Map marketing spots to their position
        // so we can do a cheap lookup
        const withKeyedPosition = marketingContent?.reduce<Record<string, MarketingSpotType>>(
            (acc, spot) => {
                acc[spot.position ?? 1] = spot;
                return acc;
            },
            {}
        );

        // Insert marketing spots into the hits array
        let pushedSpots = 0;
        const formattedHits = hits.reduce((acc: (AlgoliaHit | MarketingSpotType)[], hit, index) => {
            const marketingSpot = withKeyedPosition?.[index + pushedSpots + 1];
            if (marketingSpot) {
                pushedSpots++;
                acc.push(marketingSpot, hit);
            } else {
                acc.push(hit);
            }
            return acc;
        }, []);

        return formattedHits;
    }, [hits, marketingContent]);

    if (isZeroResults) {
        return (
            <div>
                <Headline as="h2">{translate('commerce.noFilterResult')}</Headline>
            </div>
        );
    }

    return (
        <>
            <StyledProductGrid
                gridLayout={gridLayout}
                marketingContent={Boolean(marketingContent && marketingContent.length > 0)}
            >
                {hitsWithPotientialSpots?.map((item, index) => (
                    <React.Fragment key={isHit(item) ? item.objectID : weakKey(item)}>
                        {isHit(item) && (
                            <HitWithInsights
                                hit={
                                    {
                                        ...item,
                                        // Additional data must be passed through this parameter because Algolia does not allow other props
                                        index,
                                        listType: source,
                                        isProductListPage,
                                        // Force any since AlgoliaHit is not compatible with ProductInsightsType
                                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                    } as any
                                }
                            />
                        )}
                        {isSpot(item) && <MarketingSpot marketingSpot={item} index={index} />}
                    </React.Fragment>
                ))}
            </StyledProductGrid>
            <Pagination />

            {collectionPageSchema && (
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{ __html: collectionPageSchema }}
                />
            )}
        </>
    );
};

export const ProductHits = connectPagination(connectHits(CustomHit));
